// ----------------------------------------------------------------------------

/**
 * All constants for endpoint configuration
 */

// export const APP_SERVER = "http://localhost:8081";
// export const APP_SERVER = "http://13.127.77.21:8081";

// @since NEXT_VERSION
export const APP_VERSION = process.env.REACT_APP_VERSION || 'dev';

export const FROM_DATE_PLACEHOLDER = "$fromDate$";
export const TO_DATE_PLACEHOLDER = "$toDate$";
export const REF_DATE_PLACEHOLDER = "$refDate$";
export const USER_EMAIL_PLACEHOLDER = "$userEmail$";
export const USER_ID_PLACEHOLDER = "$userId$";
export const ROLE_PLACEHOLDER = "$role$";
export const CLIENT_ID_PLACEHOLDER = "$clientId$";
export const MEMBER_ID_PLACEHOLDER = "$memberId$";
export const MANAGER_ID_PLACEHOLDER = "$managerId$";
export const ACTIVITY_ID_PLACEHOLDER = "$activityId$";
export const ACTIVITY_TYPE_ID_PLACEHOLDER = "$activityTypeId$";
// Client projects live in client-management and are a different thing from the phase-2
// project-service "project" (which owns PROJECT_ID_PLACEHOLDER further down this file).
// @since NEXT_VERSION
export const CLIENT_PROJECT_ID_PLACEHOLDER = "$clientProjectId$";
export const LEAVE_ID_PLACEHOLDER = "$leaveId$";
export const WORKING_HOUR_ID_PLACEHOLDER = "$workingHourId$";
export const CALENDAR_ID_PLACEHOLDER = "$calendarId$";
export const HOLIDAY_ID_PLACEHOLDER = "$holidayId$";
export const YEAR_PLACEHOLDER = "$year$";
export const OWNER_ID_PLACEHOLDER = "$ownerId$";
export const IS_MEMBER_PLACEHOLDER = "$isMember$";

// Sentinel dropdown value representing the logged-in manager's own timesheet/report
// (distinct from numeric member ids and from "" = All).
export const MANAGER_SELF_OPTION_VALUE = "MANAGER_SELF";

// Sentinel value for the "All clients" option in local client-filter dropdowns.
export const ALL_CLIENTS = "ALL";

// Sentinel value for the "All" option in the effort-summary User filter dropdown.
// @since NEXT_VERSION
export const ALL_USERS = "ALL_USERS";

// "People in InOpTra – Can view" share link to the HRMS user guide; a plain
// Doc.aspx link would only open for users already granted access to the file.
// @since NEXT_VERSION
export const USER_GUIDE_URL =
  "https://inoptra.sharepoint.com/:w:/s/ADA/IQBxmHALK0W3S5Gk9ZSbFNmwAYLAqPJ0Dnali2GSGPS3t1M?e=l6EpgF";

// Check if we're using reverse proxy setup (empty host/port means proxy)
const useReverseProxy = !process.env.REACT_APP_USER_AUTH_MICROSERVICE_HOST ||
                       process.env.REACT_APP_USER_AUTH_MICROSERVICE_HOST === '';

// API Base URLs - either direct service URLs or proxy paths
export const USER_AUTH_API_HOST = useReverseProxy
    ? '/api'  // Proxy path
    : `http://${process.env.REACT_APP_USER_AUTH_MICROSERVICE_HOST}:${process.env.REACT_APP_USER_AUTH_MICROSERVICE_PORT}`;

export const MEMBER_API_HOST = useReverseProxy
    ? '/api'  // Proxy path
    : `http://${process.env.REACT_APP_MEMBER_MANAGEMENT_MICROSERVICE_HOST}:${process.env.REACT_APP_MEMBER_MANAGEMENT_MICROSERVICE_PORT}`;

export const CLIENT_API_HOST = useReverseProxy
    ? '/api'  // Proxy path
    : `http://${process.env.REACT_APP_CLIENT_MANAGEMENT_MICROSERVICE_HOST}:${process.env.REACT_APP_CLIENT_MANAGEMENT_MICROSERVICE_PORT}`;

// New Intranet Portal Services
export const IQMS_API_HOST = useReverseProxy
    ? '/api'
    : `http://${process.env.REACT_APP_IQMS_MICROSERVICE_HOST}:${process.env.REACT_APP_IQMS_MICROSERVICE_PORT}`;

export const PROJECT_API_HOST = useReverseProxy
    ? '/api'
    : `http://${process.env.REACT_APP_PROJECT_MICROSERVICE_HOST}:${process.env.REACT_APP_PROJECT_MICROSERVICE_PORT}`;

export const KNOWLEDGE_API_HOST = useReverseProxy
    ? '/api'
    : `http://${process.env.REACT_APP_KNOWLEDGE_MICROSERVICE_HOST}:${process.env.REACT_APP_KNOWLEDGE_MICROSERVICE_PORT}`;

export const SEARCH_API_HOST = useReverseProxy
    ? '/api'
    : `http://${process.env.REACT_APP_SEARCH_MICROSERVICE_HOST}:${process.env.REACT_APP_SEARCH_MICROSERVICE_PORT}`;

export const NOTIFICATION_API_HOST = useReverseProxy
    ? '/api'
    : `http://${process.env.REACT_APP_NOTIFICATION_MICROSERVICE_HOST}:${process.env.REACT_APP_NOTIFICATION_MICROSERVICE_PORT}`;


export const USER_AUTH_API_VERSION = "v1";
export const MEMBER_API_VERSION = "v1";
export const CLIENT_API_VERSION = "v1";
export const IQMS_API_VERSION = "v1";
export const PROJECT_API_VERSION = "v1";
export const KNOWLEDGE_API_VERSION = "v1";
export const SEARCH_API_VERSION = "v1";
export const NOTIFICATION_API_VERSION = "v1";
/**
 * USER-AUTH-MICROSERVICE
 */
export const USERS_ROOT_ENDPOINT = `users`;
export const GET_USER_DETAILS_BY_EMAIL = `${USER_AUTH_API_HOST}/${USERS_ROOT_ENDPOINT}/by-email?email=${USER_EMAIL_PLACEHOLDER}`;
export const CREATE_USER_ENDPOINT = `${USER_AUTH_API_HOST}/${USERS_ROOT_ENDPOINT}/save`;
export const UPDATE_USER_ENDPOINT = `${USER_AUTH_API_HOST}/${USERS_ROOT_ENDPOINT}/${USER_ID_PLACEHOLDER}`;
export const DELETE_USER_ENDPOINT = `${USER_AUTH_API_HOST}/${USERS_ROOT_ENDPOINT}/${USER_ID_PLACEHOLDER}`;
export const FETCH_ALL_USERS_ENDPOINT = `${USER_AUTH_API_HOST}/${USERS_ROOT_ENDPOINT}/all`;
export const FETCH_USERS_BY_ROLE_ENDPOINT = `${USER_AUTH_API_HOST}/${USERS_ROOT_ENDPOINT}/by-role/${ROLE_PLACEHOLDER}`;
// @since NEXT_VERSION
export const USAGE_EVENTS_ENDPOINT = `${USER_AUTH_API_HOST}/analytics/events`;
// @since NEXT_VERSION
export const USAGE_SUMMARY_ENDPOINT = `${USER_AUTH_API_HOST}/analytics/summary`;

// @since NEXT_VERSION — Department master data (HRMS-247)
export const DEPARTMENT_ID_PLACEHOLDER = "$departmentId$";
// Bare resource name, matching USERS_ROOT_ENDPOINT above — NOT "v1/api/departments". The `v1/api`
// prefix is added by setupProxy.js's rewrite(), so baking it in here would produce
// /api/v1/api/departments and match no proxy rule.
export const DEPARTMENTS_ROOT_ENDPOINT = `departments`;
export const FETCH_ALL_DEPARTMENTS_ENDPOINT = `${USER_AUTH_API_HOST}/${DEPARTMENTS_ROOT_ENDPOINT}/all`;
export const CREATE_DEPARTMENT_ENDPOINT = `${USER_AUTH_API_HOST}/${DEPARTMENTS_ROOT_ENDPOINT}/save`;
export const UPDATE_DEPARTMENT_ENDPOINT = `${USER_AUTH_API_HOST}/${DEPARTMENTS_ROOT_ENDPOINT}/${DEPARTMENT_ID_PLACEHOLDER}`;
export const DELETE_DEPARTMENT_ENDPOINT = `${USER_AUTH_API_HOST}/${DEPARTMENTS_ROOT_ENDPOINT}/${DEPARTMENT_ID_PLACEHOLDER}`;

export const AUTH_ROOT_ENDPOINT = `auth`;
export const POST_LOGIN_ENDPOINT = `${USER_AUTH_API_HOST}/${AUTH_ROOT_ENDPOINT}/authenticate`;
export const CHANGE_PASSWORD_ENDPOINT = `${USER_AUTH_API_HOST}/${USERS_ROOT_ENDPOINT}/${USER_ID_PLACEHOLDER}/change-password`;
export const FORGOT_PASSWORD_ENDPOINT = `${USER_AUTH_API_HOST}/${AUTH_ROOT_ENDPOINT}/forgot-password`;
export const VERIFY_OTP_ENDPOINT = `${USER_AUTH_API_HOST}/${AUTH_ROOT_ENDPOINT}/verify-otp`;
export const RESET_PASSWORD_ENDPOINT = `${USER_AUTH_API_HOST}/${AUTH_ROOT_ENDPOINT}/reset-password`;
export const ADMIN_INITIATE_RESET_ENDPOINT = `${USER_AUTH_API_HOST}/${AUTH_ROOT_ENDPOINT}/admin/initiate-reset`;

/**
 * MEMBER-MANAGEMENT-MICROSERVICE
*/
export const MEMBERS_ROOT_ENDPOINT = `members`;
export const CREATE_MEMBER_ENDPOINT = `${MEMBER_API_HOST}/${MEMBERS_ROOT_ENDPOINT}/save`;
export const UPDATE_MEMBER_ENDPOINT = `${MEMBER_API_HOST}/${MEMBERS_ROOT_ENDPOINT}/${MEMBER_ID_PLACEHOLDER}`;
export const DELETE_MEMBER_ENDPOINT = `${MEMBER_API_HOST}/${MEMBERS_ROOT_ENDPOINT}/${MEMBER_ID_PLACEHOLDER}`;
export const GET_MEMBER_FOR_USER_ENDPOINT = `${MEMBER_API_HOST}/${MEMBERS_ROOT_ENDPOINT}/by-user/${USER_ID_PLACEHOLDER}`;
export const GET_MEMBERS_FOR_CLIENTS_ENDPOINT = `${MEMBER_API_HOST}/${MEMBERS_ROOT_ENDPOINT}/all`;
export const GET_MEMBERS_PAGED_ENDPOINT = `${MEMBER_API_HOST}/${MEMBERS_ROOT_ENDPOINT}/all/paged`;
export const GET_MEMBERS_FOR_CLIENT_ENDPOINT = `${MEMBER_API_HOST}/${MEMBERS_ROOT_ENDPOINT}/by-client/${CLIENT_ID_PLACEHOLDER}`;
export const GET_MEMBERS_FOR_MANAGER_ENDPOINT = `${MEMBER_API_HOST}/${MEMBERS_ROOT_ENDPOINT}/by-manager/${MANAGER_ID_PLACEHOLDER}`;
// Legacy (classic) timesheet format. Additive; delete these two to drop the legacy export.
// One timesheet endpoint for both id spaces and both formats; ownerType and legacyFormat pick
// the variant. Query string is assembled per call, so this is a bare path.
// @since NEXT_VERSION
export const GET_DOWNLOAD_TIMESHEET_ENDPOINT = `${MEMBER_API_HOST}/${MEMBERS_ROOT_ENDPOINT}/downloads/timesheet`;
// Consolidated format: one workbook covering every member in the selected manager's scope at one
// client, each member a column group. Scope-driven, so it takes managerIds + clientId rather than an
// owner — which is why it is a separate endpoint rather than another flag on the one above.
// @since NEXT_VERSION
export const GET_DOWNLOAD_CONSOLIDATED_TIMESHEET_ENDPOINT = `${MEMBER_API_HOST}/${MEMBERS_ROOT_ENDPOINT}/downloads/timesheet/consolidated`;
// Concise Monthly (finance) format: one member, a row per activity category and a column per
// calendar day of the month. Owner-driven, so it takes a memberId and derives the client from that
// member's reporting record -- a per-member report cannot straddle clients without the Client Code
// column becoming ambiguous.
// @since NEXT_VERSION
export const GET_DOWNLOAD_CONCISE_MONTHLY_ENDPOINT = `${MEMBER_API_HOST}/${MEMBERS_ROOT_ENDPOINT}/downloads/timesheet/concise-monthly`;
// Org-level Utilization Report (HRMS-319): every person x client for one month. Admin-only and
// unscoped, so it takes only a year and month.
// @since NEXT_VERSION
export const GET_DOWNLOAD_UTILIZATION_REPORT_ENDPOINT = `${MEMBER_API_HOST}/${MEMBERS_ROOT_ENDPOINT}/downloads/org/utilization`;

export const MEMBER_REPORTING_ROOT_ENDPOINT = `${MEMBER_API_HOST}/${MEMBERS_ROOT_ENDPOINT}/reporting`;
export const GET_MEMBER_REPORTING_ENDPOINT = `${MEMBER_API_HOST}/${MEMBERS_ROOT_ENDPOINT}/reporting/all`;
export const GET_MEMBER_REPORTING_PAGED_ENDPOINT = `${MEMBER_API_HOST}/${MEMBERS_ROOT_ENDPOINT}/reporting/all/paged`;
export const CREATE_MEMBER_REPORTING_ENDPOINT = `${MEMBER_API_HOST}/${MEMBERS_ROOT_ENDPOINT}/reporting`;
export const UPDATE_MEMBER_REPORTING_ENDPOINT = `${MEMBER_API_HOST}/${MEMBERS_ROOT_ENDPOINT}/${MEMBER_ID_PLACEHOLDER}/reporting`;
export const DELETE_MEMBER_REPORTING_ENDPOINT = `${MEMBER_API_HOST}/${MEMBERS_ROOT_ENDPOINT}/${MEMBER_ID_PLACEHOLDER}/reporting`;

export const ACTIVITIES_ROOT_ENDPOINT = `activities`;
export const GET_ACTIVITIES_BETWEEN_FOR_CLIENT_ENDPOINT = `${MEMBER_API_HOST}/${ACTIVITIES_ROOT_ENDPOINT}/by-client/${CLIENT_ID_PLACEHOLDER}/between?fromDate=${FROM_DATE_PLACEHOLDER}&toDate=${TO_DATE_PLACEHOLDER}&pageNumber=0&pageSize=100`;
export const GET_ACTIVITIES_BETWEEN_FOR_MANAGER_ENDPOINT = `${MEMBER_API_HOST}/${ACTIVITIES_ROOT_ENDPOINT}/by-manager/${MANAGER_ID_PLACEHOLDER}/between?fromDate=${FROM_DATE_PLACEHOLDER}&toDate=${TO_DATE_PLACEHOLDER}&pageNumber=0&pageSize=20`;
// A manager's own activities, without their team's. // @since NEXT_VERSION
export const GET_OWN_ACTIVITIES_BETWEEN_FOR_MANAGER_ENDPOINT = `${MEMBER_API_HOST}/${ACTIVITIES_ROOT_ENDPOINT}/by-manager/${MANAGER_ID_PLACEHOLDER}/own/between?fromDate=${FROM_DATE_PLACEHOLDER}&toDate=${TO_DATE_PLACEHOLDER}&pageNumber=0&pageSize=100`;
export const GET_ACTIVITIES_BETWEEN_FOR_CLIENTS_ENDPOINT = `${MEMBER_API_HOST}/${ACTIVITIES_ROOT_ENDPOINT}/between?fromDate=${FROM_DATE_PLACEHOLDER}&toDate=${TO_DATE_PLACEHOLDER}&pageNumber=0&pageSize=20`;
export const GET_ACTIVITIES_BETWEEN_DATES_BY_MEMBER_ID_ENDPOINT = `${MEMBER_API_HOST}/${ACTIVITIES_ROOT_ENDPOINT}/by-member/${MEMBER_ID_PLACEHOLDER}/between?fromDate=${FROM_DATE_PLACEHOLDER}&toDate=${TO_DATE_PLACEHOLDER}&pageNumber=0&pageSize=100`;
export const DELETE_ACTIVITY_FOR_MEMBER_ENDPOINT = `${MEMBER_API_HOST}/${ACTIVITIES_ROOT_ENDPOINT}/${ACTIVITY_ID_PLACEHOLDER}`;
export const FETCH_SINGLE_ACTIVITY_FOR_MEMBER_ENDPOINT = `${MEMBER_API_HOST}/${ACTIVITIES_ROOT_ENDPOINT}/${ACTIVITY_ID_PLACEHOLDER}`;
export const ADD_ACTIVITY_FOR_MEMBER_ENDPOINT = `${MEMBER_API_HOST}/${ACTIVITIES_ROOT_ENDPOINT}/save`;
// Saves a batch of activities in one transaction. @since NEXT_VERSION
export const BULK_ADD_ACTIVITY_ENDPOINT = `${MEMBER_API_HOST}/${ACTIVITIES_ROOT_ENDPOINT}/bulk`;
// Hours already saved per day for one owner, for the bulk dialog's day badges. @since NEXT_VERSION
export const GET_ACTIVITY_DAY_HOURS_ENDPOINT = `${MEMBER_API_HOST}/${ACTIVITIES_ROOT_ENDPOINT}/day-hours`;
export const UPDATE_SINGLE_ACTIVITY_FOR_MEMBER_ENDPOINT = `${MEMBER_API_HOST}/${ACTIVITIES_ROOT_ENDPOINT}/${ACTIVITY_ID_PLACEHOLDER}`;
export const GET_ACTIVITY_APPROVAL_ENDPOINT = `${MEMBER_API_HOST}/${ACTIVITIES_ROOT_ENDPOINT}/approval`;

export const ACTIVITY_TYPE_ROOT_ENDPOINT = `activity-types`;
export const GET_ACTIVITY_TYPES_ENDPOINT = `${CLIENT_API_HOST}/${ACTIVITY_TYPE_ROOT_ENDPOINT}`;
export const GET_ACTIVITY_TYPES_BY_CLIENT_ENDPOINT = `${CLIENT_API_HOST}/${ACTIVITY_TYPE_ROOT_ENDPOINT}/by-client/${CLIENT_ID_PLACEHOLDER}`;
export const ADD_ACTIVITY_TYPE_ENDPOINT = `${CLIENT_API_HOST}/${ACTIVITY_TYPE_ROOT_ENDPOINT}`;
export const UPDATE_ACTIVITY_TYPE_ENDPOINT = `${CLIENT_API_HOST}/${ACTIVITY_TYPE_ROOT_ENDPOINT}/${ACTIVITY_TYPE_ID_PLACEHOLDER}`;
export const DELETE_ACTIVITY_TYPE_ENDPOINT = `${CLIENT_API_HOST}/${ACTIVITY_TYPE_ROOT_ENDPOINT}/${ACTIVITY_TYPE_ID_PLACEHOLDER}`;

// Client projects (HRMS-259) — the project an activity is logged against. Served by
// client-management alongside activity types, and named CLIENT_PROJECT_* throughout to keep it
// distinct from the phase-2 project-service endpoints in the PROJECT-SERVICE block below.
// @since NEXT_VERSION
export const CLIENT_PROJECT_ROOT_ENDPOINT = `projects`;
// @since NEXT_VERSION
export const GET_CLIENT_PROJECTS_ENDPOINT = `${CLIENT_API_HOST}/${CLIENT_PROJECT_ROOT_ENDPOINT}`;
// activeOnly=true so a deactivated project cannot be picked for new work. @since NEXT_VERSION
export const GET_ACTIVE_CLIENT_PROJECTS_BY_CLIENT_ENDPOINT = `${CLIENT_API_HOST}/${CLIENT_PROJECT_ROOT_ENDPOINT}/by-client/${CLIENT_ID_PLACEHOLDER}?activeOnly=true`;
// Unfiltered, for the master data grid where deactivated projects must stay visible. @since NEXT_VERSION
export const GET_CLIENT_PROJECTS_BY_CLIENT_ENDPOINT = `${CLIENT_API_HOST}/${CLIENT_PROJECT_ROOT_ENDPOINT}/by-client/${CLIENT_ID_PLACEHOLDER}`;
// @since NEXT_VERSION
export const ADD_CLIENT_PROJECT_ENDPOINT = `${CLIENT_API_HOST}/${CLIENT_PROJECT_ROOT_ENDPOINT}`;
// @since NEXT_VERSION
export const UPDATE_CLIENT_PROJECT_ENDPOINT = `${CLIENT_API_HOST}/${CLIENT_PROJECT_ROOT_ENDPOINT}/${CLIENT_PROJECT_ID_PLACEHOLDER}`;

export const LEAVE_PLANS_ROOT_ENDPOINT = `leave-plans`;
export const GET_LEAVE_PLANS_BETWEEN_FOR_MEMBER_ENDPOINT = `${MEMBER_API_HOST}/${LEAVE_PLANS_ROOT_ENDPOINT}/by-member/${MEMBER_ID_PLACEHOLDER}?fromDate=${FROM_DATE_PLACEHOLDER}&toDate=${TO_DATE_PLACEHOLDER}`;
export const GET_LEAVE_PLANS_BETWEEN_FOR_MANAGER_ENDPOINT = `${MEMBER_API_HOST}/${LEAVE_PLANS_ROOT_ENDPOINT}/by-manager/${MANAGER_ID_PLACEHOLDER}?fromDate=${FROM_DATE_PLACEHOLDER}&toDate=${TO_DATE_PLACEHOLDER}`;
export const GET_LEAVE_PLANS_BETWEEN_FOR_CLIENT_ENDPOINT = `${MEMBER_API_HOST}/${LEAVE_PLANS_ROOT_ENDPOINT}/by-client/${CLIENT_ID_PLACEHOLDER}?fromDate=${FROM_DATE_PLACEHOLDER}&toDate=${TO_DATE_PLACEHOLDER}`;
export const GET_ALL_LEAVE_PLANS_BETWEEN_DATES_ENDPOINT= `${MEMBER_API_HOST}/${LEAVE_PLANS_ROOT_ENDPOINT}/all?fromDate=${FROM_DATE_PLACEHOLDER}&toDate=${TO_DATE_PLACEHOLDER}`;
export const ADD_LEAVE_PLAN_FOR_MEMBER_ENDPOINT = `${MEMBER_API_HOST}/${LEAVE_PLANS_ROOT_ENDPOINT}/save`;
export const UPDATE_LEAVE_PLAN_FOR_MEMBER_ENDPOINT = `${MEMBER_API_HOST}/${LEAVE_PLANS_ROOT_ENDPOINT}/${LEAVE_ID_PLACEHOLDER}`;
// @since NEXT_VERSION
export const GET_LEAVE_PLAN_BY_ID_ENDPOINT = `${MEMBER_API_HOST}/${LEAVE_PLANS_ROOT_ENDPOINT}/${LEAVE_ID_PLACEHOLDER}`;
export const DELETE_LEAVE_PLAN_FOR_MEMBER_ENDPOINT = `${MEMBER_API_HOST}/${LEAVE_PLANS_ROOT_ENDPOINT}/${LEAVE_ID_PLACEHOLDER}`;
export const APPROVE_LEAVE_PLAN_ENDPOINT = `${MEMBER_API_HOST}/${LEAVE_PLANS_ROOT_ENDPOINT}/${LEAVE_ID_PLACEHOLDER}/approve`;
export const REJECT_LEAVE_PLAN_ENDPOINT = `${MEMBER_API_HOST}/${LEAVE_PLANS_ROOT_ENDPOINT}/${LEAVE_ID_PLACEHOLDER}/reject`;


export const HOLIDAYS_ROOT_ENDPOINT = `holidays`;
export const GET_APPLICABLE_HOLIDAYS_ENDPOINT = `${MEMBER_API_HOST}/${HOLIDAYS_ROOT_ENDPOINT}/applicable?fromDate=${FROM_DATE_PLACEHOLDER}&toDate=${TO_DATE_PLACEHOLDER}`;
export const GET_OPTIONAL_HOLIDAYS_ENDPOINT = `${MEMBER_API_HOST}/${HOLIDAYS_ROOT_ENDPOINT}/optional?ownerId=${OWNER_ID_PLACEHOLDER}&isMember=${IS_MEMBER_PLACEHOLDER}&year=${YEAR_PLACEHOLDER}`;
export const HOLIDAY_ELECTION_ENDPOINT = `${MEMBER_API_HOST}/${HOLIDAYS_ROOT_ENDPOINT}/${HOLIDAY_ID_PLACEHOLDER}/elections?ownerId=${OWNER_ID_PLACEHOLDER}&isMember=${IS_MEMBER_PLACEHOLDER}`;

export const HOLIDAY_CALENDARS_ROOT_ENDPOINT = `holiday-calendars`;
export const GET_HOLIDAY_CALENDARS_ENDPOINT = `${MEMBER_API_HOST}/${HOLIDAY_CALENDARS_ROOT_ENDPOINT}`;
export const GET_CALENDAR_HOLIDAYS_ENDPOINT = `${MEMBER_API_HOST}/${HOLIDAY_CALENDARS_ROOT_ENDPOINT}/${CALENDAR_ID_PLACEHOLDER}/holidays`;
export const GET_CALENDAR_HOLIDAYS_PAGED_ENDPOINT = `${MEMBER_API_HOST}/${HOLIDAY_CALENDARS_ROOT_ENDPOINT}/${CALENDAR_ID_PLACEHOLDER}/holidays/paged`;
export const CREATE_CALENDAR_HOLIDAY_ENDPOINT = `${MEMBER_API_HOST}/${HOLIDAY_CALENDARS_ROOT_ENDPOINT}/${CALENDAR_ID_PLACEHOLDER}/holidays`;
export const UPDATE_CALENDAR_HOLIDAY_ENDPOINT = `${MEMBER_API_HOST}/${HOLIDAY_CALENDARS_ROOT_ENDPOINT}/${CALENDAR_ID_PLACEHOLDER}/holidays/${HOLIDAY_ID_PLACEHOLDER}`;
export const DELETE_CALENDAR_HOLIDAY_ENDPOINT = `${MEMBER_API_HOST}/${HOLIDAY_CALENDARS_ROOT_ENDPOINT}/${CALENDAR_ID_PLACEHOLDER}/holidays/${HOLIDAY_ID_PLACEHOLDER}`;

export const EFFORT_SUMMARY_ROOT_ENDPOINT = `efforts/summary`;
export const GET_EFFORT_SUMMARY_ENDPOINT = `${MEMBER_API_HOST}/${EFFORT_SUMMARY_ROOT_ENDPOINT}?startDate=${FROM_DATE_PLACEHOLDER}&endDate=${TO_DATE_PLACEHOLDER}`;

export const LEAVE_PLANS_HOLIDAYS_ROOT_ENDPOINT = `leave-plans-holidays`;
export const GET_LEAVE_PLANS_HOLIDAYS_BETWEEN_DATES_ENDPOINT = `${MEMBER_API_HOST}/${LEAVE_PLANS_HOLIDAYS_ROOT_ENDPOINT}/between?fromDate=${FROM_DATE_PLACEHOLDER}&toDate=${TO_DATE_PLACEHOLDER}`;
export const GET_LEAVE_PLANS_HOLIDAYS_FOR_CURRENT_MONTH_ENDPOINT = `${MEMBER_API_HOST}/${LEAVE_PLANS_HOLIDAYS_ROOT_ENDPOINT}/current-month`;
export const GET_LEAVE_PLANS_HOLIDAYS_BY_MEMBER_BETWEEN_DATES_ENDPOINT = `${MEMBER_API_HOST}/${LEAVE_PLANS_HOLIDAYS_ROOT_ENDPOINT}/by-member/${MEMBER_ID_PLACEHOLDER}/between?fromDate=${FROM_DATE_PLACEHOLDER}&toDate=${TO_DATE_PLACEHOLDER}`;
export const GET_LEAVE_PLANS_HOLIDAYS_BY_MEMBER_FOR_CURRENT_MONTH_ENDPOINT = `${MEMBER_API_HOST}/${LEAVE_PLANS_HOLIDAYS_ROOT_ENDPOINT}/by-member/${MEMBER_ID_PLACEHOLDER}/current-month`;
export const GET_LEAVE_PLANS_HOLIDAYS_MONTH_OF_DATE_ENDPOINT = `${MEMBER_API_HOST}/${LEAVE_PLANS_HOLIDAYS_ROOT_ENDPOINT}/month-of-date/${REF_DATE_PLACEHOLDER}`;
export const GET_LEAVE_PLANS_HOLIDAYS_BY_MEMBER_MONTH_OF_DATE_ENDPOINT = `${MEMBER_API_HOST}/${LEAVE_PLANS_HOLIDAYS_ROOT_ENDPOINT}/by-member/${MEMBER_ID_PLACEHOLDER}/month-of-date/${REF_DATE_PLACEHOLDER}`;

export const ACTIVITY_STATS_ROOT_ENDPOINT = `activity-stats`;
export const GET_ACTIVITY_STATS_WEEKLY_BETWEEN_DATES_ENDPOINT = `${MEMBER_API_HOST}/${ACTIVITY_STATS_ROOT_ENDPOINT}/weekly/between?fromDate=${FROM_DATE_PLACEHOLDER}&toDate=${TO_DATE_PLACEHOLDER}`;
export const GET_ACTIVITY_STATS_MONTHLY_BETWEEN_DATES_ENDPOINT = `${MEMBER_API_HOST}/${ACTIVITY_STATS_ROOT_ENDPOINT}/monthly/between?fromDate=${FROM_DATE_PLACEHOLDER}&toDate=${TO_DATE_PLACEHOLDER}`;
export const GET_ACTIVITY_STATS_WEEKLY_FOR_CURRENT_MONTH_ENDPOINT = `${MEMBER_API_HOST}/${ACTIVITY_STATS_ROOT_ENDPOINT}/weekly/current-month`;
export const GET_ACTIVITY_STATS_MONTHLY_FOR_CURRENT_MONTH_ENDPOINT = `${MEMBER_API_HOST}/${ACTIVITY_STATS_ROOT_ENDPOINT}/monthly/current-month`;
export const GET_ACTIVITY_STATS_WEEKLY_BY_OWNER_BETWEEN_DATES_ENDPOINT = `${MEMBER_API_HOST}/${ACTIVITY_STATS_ROOT_ENDPOINT}/weekly/by-owner/${MEMBER_ID_PLACEHOLDER}/between?fromDate=${FROM_DATE_PLACEHOLDER}&toDate=${TO_DATE_PLACEHOLDER}`;
export const GET_ACTIVITY_STATS_MONTHLY_BY_OWNER_BETWEEN_DATES_ENDPOINT = `${MEMBER_API_HOST}/${ACTIVITY_STATS_ROOT_ENDPOINT}/monthly/by-owner/${MEMBER_ID_PLACEHOLDER}/between?fromDate=${FROM_DATE_PLACEHOLDER}&toDate=${TO_DATE_PLACEHOLDER}`;
export const GET_ACTIVITY_STATS_WEEKLY_BY_OWNER_FOR_CURRENT_MONTH_ENDPOINT = `${MEMBER_API_HOST}/${ACTIVITY_STATS_ROOT_ENDPOINT}/weekly/current-month/by-owner/${MEMBER_ID_PLACEHOLDER}`;
export const GET_ACTIVITY_STATS_MONTHLY_BY_OWNER_FOR_CURRENT_MONTH_ENDPOINT = `${MEMBER_API_HOST}/${ACTIVITY_STATS_ROOT_ENDPOINT}/monthly/current-month/by-owner/${MEMBER_ID_PLACEHOLDER}`;
export const GET_ACTIVITY_STATS_WEEKLY_CONSOLIDATED_BETWEEN_DATES_ENDPOINT = `${MEMBER_API_HOST}/${ACTIVITY_STATS_ROOT_ENDPOINT}/weekly/consolidated/between?fromDate=${FROM_DATE_PLACEHOLDER}&toDate=${TO_DATE_PLACEHOLDER}`;
export const GET_ACTIVITY_STATS_MONTHLY_CONSOLIDATED_BETWEEN_DATES_ENDPOINT = `${MEMBER_API_HOST}/${ACTIVITY_STATS_ROOT_ENDPOINT}/monthly/consolidated/between?fromDate=${FROM_DATE_PLACEHOLDER}&toDate=${TO_DATE_PLACEHOLDER}`;
export const GET_ACTIVITY_STATS_WEEKLY_CONSOLIDATED_FOR_CURRENT_MONTH_ENDPOINT = `${MEMBER_API_HOST}/${ACTIVITY_STATS_ROOT_ENDPOINT}/weekly/current-month/consolidated`;
export const GET_ACTIVITY_STATS_MONTHLY_CONSOLIDATED_FOR_CURRENT_MONTH_ENDPOINT = `${MEMBER_API_HOST}/${ACTIVITY_STATS_ROOT_ENDPOINT}/monthly/current-month/consolidated`;
export const GET_ACTIVITY_STATS_MONTHLY_BY_CLIENT_BETWEEN_ENDPOINT = `${MEMBER_API_HOST}/${ACTIVITY_STATS_ROOT_ENDPOINT}/monthly/by-client/${CLIENT_ID_PLACEHOLDER}/between?fromDate=${FROM_DATE_PLACEHOLDER}&toDate=${TO_DATE_PLACEHOLDER}`;

export const REPORT_ROOT_ENDPOINT = `reports`;
// Unified consolidated-effort endpoints. Query string is assembled per call (every filter is
// optional), so unlike the placeholder-substitution constants above these are bare paths.
// @since NEXT_VERSION
export const GET_UNIFIED_EFFORTS_ENDPOINT = `${MEMBER_API_HOST}/${REPORT_ROOT_ENDPOINT}/efforts`;
// @since NEXT_VERSION
export const GET_UNIFIED_EFFORTS_MONTHLY_ENDPOINT = `${MEMBER_API_HOST}/${REPORT_ROOT_ENDPOINT}/efforts/monthly`;
// Leave plans over the same scope. Keeps the LeavePlan response shape — the effort projection
// drops leaveDuration and workingDays, which the leave grid needs.
// @since NEXT_VERSION
export const GET_UNIFIED_LEAVE_ENDPOINT = `${MEMBER_API_HOST}/${REPORT_ROOT_ENDPOINT}/leave`;
// Owner id spaces for the unified endpoints — mirrors the backend OwnerType enum.
// @since NEXT_VERSION
export const OWNER_TYPE_MEMBER = "MEMBER";
// @since NEXT_VERSION
export const OWNER_TYPE_MANAGER = "MANAGER";
/**
 * CLIENT-MANAGEMENT-MICROSERVICE
 */
export const CLIENTS_ROOT_ENDPOINT = `clients`;
export const GET_ALL_CLIENTS_ENDPOINT = `${CLIENT_API_HOST}/${CLIENTS_ROOT_ENDPOINT}/all`;
export const GET_CLIENTS_PAGED_ENDPOINT = `${CLIENT_API_HOST}/${CLIENTS_ROOT_ENDPOINT}/all/paged`;
export const CREATE_CLIENT_ENDPOINT = `${CLIENT_API_HOST}/${CLIENTS_ROOT_ENDPOINT}/save`;
export const UPDATE_CLIENT_ENDPOINT = `${CLIENT_API_HOST}/${CLIENTS_ROOT_ENDPOINT}/${CLIENT_ID_PLACEHOLDER}`;
export const DELETE_CLIENT_ENDPOINT = `${CLIENT_API_HOST}/${CLIENTS_ROOT_ENDPOINT}/${CLIENT_ID_PLACEHOLDER}`;
export const GET_CLIENT_BY_CLIENT_USER_ID_ENDPOINT = `${CLIENT_API_HOST}/${CLIENTS_ROOT_ENDPOINT}/by-client-user/${USER_ID_PLACEHOLDER}`;
export const GET_CLIENT_BY_MEMBER_USER_ID_ENDPOINT = `${CLIENT_API_HOST}/${CLIENTS_ROOT_ENDPOINT}/by-member-user/${USER_ID_PLACEHOLDER}`;

export const CLIENT_MAPPING_ROOT_ENDPOINT = `${CLIENT_API_HOST}/${CLIENTS_ROOT_ENDPOINT}`;
export const GET_CLIENT_MAPPING_ENDPOINT = `${CLIENT_MAPPING_ROOT_ENDPOINT}/users/all`;
export const GET_CLIENT_MAPPING_PAGED_ENDPOINT = `${CLIENT_MAPPING_ROOT_ENDPOINT}/users/all/paged`;
export const CREATE_CLIENT_MAPPING_ENDPOINT = `${CLIENT_MAPPING_ROOT_ENDPOINT}/map`;
export const UPDATE_CLIENT_MAPPING_ENDPOINT = `${CLIENT_MAPPING_ROOT_ENDPOINT}/map`;
export const DELETE_CLIENT_MAPPING_ENDPOINT = `${CLIENT_MAPPING_ROOT_ENDPOINT}/${CLIENT_ID_PLACEHOLDER}/unmap/${USER_ID_PLACEHOLDER}`;

// BDM (Business Development Manager) to client mapping — @since NEXT_VERSION (HRMS-116)
export const GET_CLIENTS_FOR_BDM_ENDPOINT = `${CLIENT_MAPPING_ROOT_ENDPOINT}/bdm/clients/by-bdm/${USER_ID_PLACEHOLDER}`;

export const WORKING_HOURS_ENDPOINT = `${MEMBER_API_HOST}/${CLIENTS_ROOT_ENDPOINT}/working-hours`;
export const GET_ALL_WORKING_HOURS_ENDPOINT = `${WORKING_HOURS_ENDPOINT}`;
export const CREATE_WORKING_HOURS_ENDPOINT = `${WORKING_HOURS_ENDPOINT}`;
export const UPDATE_WORKING_HOURS_ENDPOINT = `${WORKING_HOURS_ENDPOINT}/${WORKING_HOUR_ID_PLACEHOLDER}`;
export const DELETE_WORKING_HOURS_ENDPOINT = `${WORKING_HOURS_ENDPOINT}/${WORKING_HOUR_ID_PLACEHOLDER}`;
export const GET_LOCATIONS_ENDPOINT = `${WORKING_HOURS_ENDPOINT}/locations`;
export const GET_WORKING_HOURS_BY_CLIENT_ENDPOINT = `${WORKING_HOURS_ENDPOINT}/by-client/${CLIENT_ID_PLACEHOLDER}`;

export const MANAGERS_ROOT_ENDPOINT = `managers`;
export const GET_MANAGER_BY_USER_ID_ENDPOINT = `${CLIENT_API_HOST}/${MANAGERS_ROOT_ENDPOINT}/by-user/${USER_ID_PLACEHOLDER}`;
export const GET_ALL_MANAGERS = `${CLIENT_API_HOST}/${MANAGERS_ROOT_ENDPOINT}/all`;
export const GET_MANAGERS_PAGED_ENDPOINT = `${CLIENT_API_HOST}/${MANAGERS_ROOT_ENDPOINT}/all/paged`;
export const CREATE_MANAGER_ENDPOINT = `${CLIENT_API_HOST}/${MANAGERS_ROOT_ENDPOINT}/save`;
export const UPDATE_MANAGER_ENDPOINT = `${CLIENT_API_HOST}/${MANAGERS_ROOT_ENDPOINT}/${MANAGER_ID_PLACEHOLDER}`;
export const DELETE_MANAGER_ENDPOINT = `${CLIENT_API_HOST}/${MANAGERS_ROOT_ENDPOINT}/${MANAGER_ID_PLACEHOLDER}`;

/**
 * IQMS-SERVICE (Quality Management)
 */
export const PROCESS_ID_PLACEHOLDER = "$processId$";
export const AUDIT_ID_PLACEHOLDER = "$auditId$";
export const CAPA_ID_PLACEHOLDER = "$capaId$";
export const RISK_ID_PLACEHOLDER = "$riskId$";

export const IQMS_PROCESSES_ROOT_ENDPOINT = `processes`;
export const GET_ALL_PROCESSES_ENDPOINT = `${IQMS_API_HOST}/${IQMS_PROCESSES_ROOT_ENDPOINT}`;
export const GET_PROCESS_BY_ID_ENDPOINT = `${IQMS_API_HOST}/${IQMS_PROCESSES_ROOT_ENDPOINT}/${PROCESS_ID_PLACEHOLDER}`;
export const CREATE_PROCESS_ENDPOINT = `${IQMS_API_HOST}/${IQMS_PROCESSES_ROOT_ENDPOINT}`;
export const UPDATE_PROCESS_ENDPOINT = `${IQMS_API_HOST}/${IQMS_PROCESSES_ROOT_ENDPOINT}/${PROCESS_ID_PLACEHOLDER}`;
export const DELETE_PROCESS_ENDPOINT = `${IQMS_API_HOST}/${IQMS_PROCESSES_ROOT_ENDPOINT}/${PROCESS_ID_PLACEHOLDER}`;

export const IQMS_AUDITS_ROOT_ENDPOINT = `audits`;
export const GET_ALL_AUDITS_ENDPOINT = `${IQMS_API_HOST}/${IQMS_AUDITS_ROOT_ENDPOINT}`;
export const GET_AUDIT_BY_ID_ENDPOINT = `${IQMS_API_HOST}/${IQMS_AUDITS_ROOT_ENDPOINT}/${AUDIT_ID_PLACEHOLDER}`;
export const CREATE_AUDIT_ENDPOINT = `${IQMS_API_HOST}/${IQMS_AUDITS_ROOT_ENDPOINT}`;
export const UPDATE_AUDIT_ENDPOINT = `${IQMS_API_HOST}/${IQMS_AUDITS_ROOT_ENDPOINT}/${AUDIT_ID_PLACEHOLDER}`;
export const DELETE_AUDIT_ENDPOINT = `${IQMS_API_HOST}/${IQMS_AUDITS_ROOT_ENDPOINT}/${AUDIT_ID_PLACEHOLDER}`;

export const IQMS_CAPA_ROOT_ENDPOINT = `capa`;
export const GET_ALL_CAPA_ENDPOINT = `${IQMS_API_HOST}/${IQMS_CAPA_ROOT_ENDPOINT}`;
export const GET_CAPA_BY_ID_ENDPOINT = `${IQMS_API_HOST}/${IQMS_CAPA_ROOT_ENDPOINT}/${CAPA_ID_PLACEHOLDER}`;
export const CREATE_CAPA_ENDPOINT = `${IQMS_API_HOST}/${IQMS_CAPA_ROOT_ENDPOINT}`;
export const UPDATE_CAPA_ENDPOINT = `${IQMS_API_HOST}/${IQMS_CAPA_ROOT_ENDPOINT}/${CAPA_ID_PLACEHOLDER}`;
export const DELETE_CAPA_ENDPOINT = `${IQMS_API_HOST}/${IQMS_CAPA_ROOT_ENDPOINT}/${CAPA_ID_PLACEHOLDER}`;

export const IQMS_RISKS_ROOT_ENDPOINT = `risks`;
export const GET_ALL_RISKS_ENDPOINT = `${IQMS_API_HOST}/${IQMS_RISKS_ROOT_ENDPOINT}`;
export const GET_RISK_BY_ID_ENDPOINT = `${IQMS_API_HOST}/${IQMS_RISKS_ROOT_ENDPOINT}/${RISK_ID_PLACEHOLDER}`;
export const CREATE_RISK_ENDPOINT = `${IQMS_API_HOST}/${IQMS_RISKS_ROOT_ENDPOINT}`;
export const UPDATE_RISK_ENDPOINT = `${IQMS_API_HOST}/${IQMS_RISKS_ROOT_ENDPOINT}/${RISK_ID_PLACEHOLDER}`;
export const DELETE_RISK_ENDPOINT = `${IQMS_API_HOST}/${IQMS_RISKS_ROOT_ENDPOINT}/${RISK_ID_PLACEHOLDER}`;

/**
 * PROJECT-SERVICE (Project Management)
 */
export const PROJECT_ID_PLACEHOLDER = "$projectId$";
export const MILESTONE_ID_PLACEHOLDER = "$milestoneId$";
export const RESOURCE_ID_PLACEHOLDER = "$resourceId$";

export const PROJECTS_ROOT_ENDPOINT = `projects`;
export const GET_ALL_PROJECTS_ENDPOINT = `${PROJECT_API_HOST}/${PROJECTS_ROOT_ENDPOINT}`;
export const GET_PROJECT_BY_ID_ENDPOINT = `${PROJECT_API_HOST}/${PROJECTS_ROOT_ENDPOINT}/${PROJECT_ID_PLACEHOLDER}`;
export const CREATE_PROJECT_ENDPOINT = `${PROJECT_API_HOST}/${PROJECTS_ROOT_ENDPOINT}`;
export const UPDATE_PROJECT_ENDPOINT = `${PROJECT_API_HOST}/${PROJECTS_ROOT_ENDPOINT}/${PROJECT_ID_PLACEHOLDER}`;
export const DELETE_PROJECT_ENDPOINT = `${PROJECT_API_HOST}/${PROJECTS_ROOT_ENDPOINT}/${PROJECT_ID_PLACEHOLDER}`;
export const GET_PROJECT_HEALTH_ENDPOINT = `${PROJECT_API_HOST}/${PROJECTS_ROOT_ENDPOINT}/${PROJECT_ID_PLACEHOLDER}/health`;

export const PROJECT_MILESTONES_ROOT_ENDPOINT = `milestones`;
export const GET_ALL_MILESTONES_ENDPOINT = `${PROJECT_API_HOST}/${PROJECT_MILESTONES_ROOT_ENDPOINT}`;
export const GET_PROJECT_MILESTONES_ENDPOINT = `${PROJECT_API_HOST}/${PROJECT_MILESTONES_ROOT_ENDPOINT}/project/${PROJECT_ID_PLACEHOLDER}`;
export const CREATE_MILESTONE_ENDPOINT = `${PROJECT_API_HOST}/${PROJECT_MILESTONES_ROOT_ENDPOINT}`;
export const UPDATE_MILESTONE_ENDPOINT = `${PROJECT_API_HOST}/${PROJECT_MILESTONES_ROOT_ENDPOINT}/${MILESTONE_ID_PLACEHOLDER}`;
export const DELETE_MILESTONE_ENDPOINT = `${PROJECT_API_HOST}/${PROJECT_MILESTONES_ROOT_ENDPOINT}/${MILESTONE_ID_PLACEHOLDER}`;

export const PROJECT_RESOURCES_ROOT_ENDPOINT = `resources`;
export const GET_ALL_RESOURCES_ENDPOINT = `${PROJECT_API_HOST}/${PROJECT_RESOURCES_ROOT_ENDPOINT}`;
export const GET_PROJECT_RESOURCES_ENDPOINT = `${PROJECT_API_HOST}/${PROJECT_RESOURCES_ROOT_ENDPOINT}/project/${PROJECT_ID_PLACEHOLDER}`;
export const CREATE_RESOURCE_ENDPOINT = `${PROJECT_API_HOST}/${PROJECT_RESOURCES_ROOT_ENDPOINT}`;
export const UPDATE_RESOURCE_ENDPOINT = `${PROJECT_API_HOST}/${PROJECT_RESOURCES_ROOT_ENDPOINT}/${RESOURCE_ID_PLACEHOLDER}`;
export const DELETE_RESOURCE_ENDPOINT = `${PROJECT_API_HOST}/${PROJECT_RESOURCES_ROOT_ENDPOINT}/${RESOURCE_ID_PLACEHOLDER}`;

/**
 * KNOWLEDGE-SERVICE (Knowledge Management)
 */
export const DOCUMENT_ID_PLACEHOLDER = "$documentId$";
export const TAG_ID_PLACEHOLDER = "$tagId$";

export const DOCUMENTS_ROOT_ENDPOINT = `documents`;
export const GET_ALL_DOCUMENTS_ENDPOINT = `${KNOWLEDGE_API_HOST}/${DOCUMENTS_ROOT_ENDPOINT}`;
export const GET_DOCUMENT_BY_ID_ENDPOINT = `${KNOWLEDGE_API_HOST}/${DOCUMENTS_ROOT_ENDPOINT}/${DOCUMENT_ID_PLACEHOLDER}`;
export const CREATE_DOCUMENT_ENDPOINT = `${KNOWLEDGE_API_HOST}/${DOCUMENTS_ROOT_ENDPOINT}`;
export const UPDATE_DOCUMENT_ENDPOINT = `${KNOWLEDGE_API_HOST}/${DOCUMENTS_ROOT_ENDPOINT}/${DOCUMENT_ID_PLACEHOLDER}`;
export const DELETE_DOCUMENT_ENDPOINT = `${KNOWLEDGE_API_HOST}/${DOCUMENTS_ROOT_ENDPOINT}/${DOCUMENT_ID_PLACEHOLDER}`;
export const DOWNLOAD_DOCUMENT_ENDPOINT = `${KNOWLEDGE_API_HOST}/${DOCUMENTS_ROOT_ENDPOINT}/${DOCUMENT_ID_PLACEHOLDER}/download`;

export const TAGS_ROOT_ENDPOINT = `tags`;
export const GET_ALL_TAGS_ENDPOINT = `${KNOWLEDGE_API_HOST}/${TAGS_ROOT_ENDPOINT}`;
export const CREATE_TAG_ENDPOINT = `${KNOWLEDGE_API_HOST}/${TAGS_ROOT_ENDPOINT}`;
export const DELETE_TAG_ENDPOINT = `${KNOWLEDGE_API_HOST}/${TAGS_ROOT_ENDPOINT}/${TAG_ID_PLACEHOLDER}`;

/**
 * SEARCH-SERVICE (Enterprise Search)
 */
export const SEARCH_ROOT_ENDPOINT = `search`;
export const SEARCH_ENDPOINT = `${SEARCH_API_HOST}/${SEARCH_ROOT_ENDPOINT}`;
export const SEARCH_BY_ENTITY_ENDPOINT = `${SEARCH_API_HOST}/${SEARCH_ROOT_ENDPOINT}/entity`;

/**
 * NOTIFICATION-SERVICE (Notifications)
 */
export const NOTIFICATION_ID_PLACEHOLDER = "$notificationId$";

export const NOTIFICATIONS_ROOT_ENDPOINT = `notifications`;
export const GET_USER_NOTIFICATIONS_ENDPOINT = `${NOTIFICATION_API_HOST}/${NOTIFICATIONS_ROOT_ENDPOINT}/user/${USER_ID_PLACEHOLDER}`;
export const GET_UNREAD_NOTIFICATIONS_ENDPOINT = `${NOTIFICATION_API_HOST}/${NOTIFICATIONS_ROOT_ENDPOINT}/user/${USER_ID_PLACEHOLDER}/unread`;
export const MARK_NOTIFICATION_READ_ENDPOINT = `${NOTIFICATION_API_HOST}/${NOTIFICATIONS_ROOT_ENDPOINT}/${NOTIFICATION_ID_PLACEHOLDER}/read`;
export const MARK_ALL_NOTIFICATIONS_READ_ENDPOINT = `${NOTIFICATION_API_HOST}/${NOTIFICATIONS_ROOT_ENDPOINT}/user/${USER_ID_PLACEHOLDER}/read-all`;
export const DELETE_NOTIFICATION_ENDPOINT = `${NOTIFICATION_API_HOST}/${NOTIFICATIONS_ROOT_ENDPOINT}/${NOTIFICATION_ID_PLACEHOLDER}`;

export const NOTIFICATION_PREFERENCES_ENDPOINT = `${NOTIFICATION_API_HOST}/${NOTIFICATIONS_ROOT_ENDPOINT}/preferences`;
export const GET_USER_PREFERENCES_ENDPOINT = `${NOTIFICATION_PREFERENCES_ENDPOINT}/user/${USER_ID_PLACEHOLDER}`;
export const UPDATE_USER_PREFERENCES_ENDPOINT = `${NOTIFICATION_PREFERENCES_ENDPOINT}/user/${USER_ID_PLACEHOLDER}`;

/**
 * SKILL MATRIX (Member Management Service Extension)
 */
export const SKILL_ID_PLACEHOLDER = "$skillId$";
export const MEMBER_SKILL_ID_PLACEHOLDER = "$memberSkillId$";
export const CERTIFICATION_ID_PLACEHOLDER = "$certificationId$";

export const SKILLS_ROOT_ENDPOINT = `skills`;
export const GET_ALL_SKILLS_ENDPOINT = `${MEMBER_API_HOST}/${SKILLS_ROOT_ENDPOINT}`;
export const GET_ACTIVE_SKILLS_ENDPOINT = `${MEMBER_API_HOST}/${SKILLS_ROOT_ENDPOINT}/active`;
export const GET_SKILL_BY_ID_ENDPOINT = `${MEMBER_API_HOST}/${SKILLS_ROOT_ENDPOINT}/${SKILL_ID_PLACEHOLDER}`;
export const GET_SKILLS_BY_CATEGORY_ENDPOINT = `${MEMBER_API_HOST}/${SKILLS_ROOT_ENDPOINT}/category`;
export const CREATE_SKILL_ENDPOINT = `${MEMBER_API_HOST}/${SKILLS_ROOT_ENDPOINT}`;
export const UPDATE_SKILL_ENDPOINT = `${MEMBER_API_HOST}/${SKILLS_ROOT_ENDPOINT}/${SKILL_ID_PLACEHOLDER}`;
export const DELETE_SKILL_ENDPOINT = `${MEMBER_API_HOST}/${SKILLS_ROOT_ENDPOINT}/${SKILL_ID_PLACEHOLDER}`;

export const MEMBER_SKILLS_ROOT_ENDPOINT = `member-skills`;
export const GET_MEMBER_SKILLS_ENDPOINT = `${MEMBER_API_HOST}/${MEMBER_SKILLS_ROOT_ENDPOINT}/member/${MEMBER_ID_PLACEHOLDER}`;
export const GET_MEMBERS_BY_SKILL_ENDPOINT = `${MEMBER_API_HOST}/${MEMBER_SKILLS_ROOT_ENDPOINT}/skill/${SKILL_ID_PLACEHOLDER}`;
export const CREATE_MEMBER_SKILL_ENDPOINT = `${MEMBER_API_HOST}/${MEMBER_SKILLS_ROOT_ENDPOINT}`;
export const UPDATE_MEMBER_SKILL_ENDPOINT = `${MEMBER_API_HOST}/${MEMBER_SKILLS_ROOT_ENDPOINT}/${MEMBER_SKILL_ID_PLACEHOLDER}`;
export const VERIFY_MEMBER_SKILL_ENDPOINT = `${MEMBER_API_HOST}/${MEMBER_SKILLS_ROOT_ENDPOINT}/${MEMBER_SKILL_ID_PLACEHOLDER}/verify`;
export const DELETE_MEMBER_SKILL_ENDPOINT = `${MEMBER_API_HOST}/${MEMBER_SKILLS_ROOT_ENDPOINT}/${MEMBER_SKILL_ID_PLACEHOLDER}`;

export const CERTIFICATIONS_ROOT_ENDPOINT = `certifications`;
export const GET_ALL_CERTIFICATIONS_ENDPOINT = `${MEMBER_API_HOST}/${CERTIFICATIONS_ROOT_ENDPOINT}`;
export const GET_MEMBER_CERTIFICATIONS_ENDPOINT = `${MEMBER_API_HOST}/${CERTIFICATIONS_ROOT_ENDPOINT}/member/${MEMBER_ID_PLACEHOLDER}`;
export const GET_EXPIRING_CERTIFICATIONS_ENDPOINT = `${MEMBER_API_HOST}/${CERTIFICATIONS_ROOT_ENDPOINT}/expiring`;
export const GET_EXPIRED_CERTIFICATIONS_ENDPOINT = `${MEMBER_API_HOST}/${CERTIFICATIONS_ROOT_ENDPOINT}/expired`;
export const CREATE_CERTIFICATION_ENDPOINT = `${MEMBER_API_HOST}/${CERTIFICATIONS_ROOT_ENDPOINT}`;
export const UPDATE_CERTIFICATION_ENDPOINT = `${MEMBER_API_HOST}/${CERTIFICATIONS_ROOT_ENDPOINT}/${CERTIFICATION_ID_PLACEHOLDER}`;
export const VERIFY_CERTIFICATION_ENDPOINT = `${MEMBER_API_HOST}/${CERTIFICATIONS_ROOT_ENDPOINT}/${CERTIFICATION_ID_PLACEHOLDER}/verify`;
export const DELETE_CERTIFICATION_ENDPOINT = `${MEMBER_API_HOST}/${CERTIFICATIONS_ROOT_ENDPOINT}/${CERTIFICATION_ID_PLACEHOLDER}`;

// ----------------------------------------------------------------------------

/**
 * All constants representing Activity statuses
 */

export const ACTIVITY_STATUSES = [
    {
        value: "OPEN",
        label: "OPEN"
    },
    {
        value: "COMPLETED",
        label: "COMPLETED"
    },
    {
        value: "IN_PROGRESS",
        label: "IN PROGRESS"
    },
    {
        value: "IN_REVIEW",
        label: "IN REVIEW"
    },
    {
        value: "IN_QA",
        label: "IN QA"
    },
    {
        value: "ON_HOLD",
        label: "ON HOLD"
    },
    {
        value: "CANCELLED",
        label: "CANCELLED"
    },
    {
        value: "CLOSED",
        label: "CLOSED"
    },
];


export const LEAVE_PLAN_STATUSES = [
    {
        value: "APPLIED",
        label: "APPLIED"
    },
    {
        value: "CANCELLED",
        label: "CANCELLED"
    },
];

export const LEAVE_PLAN_TYPES = [
    {
        value: "LEAVE",
        label: "LEAVE"
    },
    {
        value: "COMPANY_HOLIDAY",
        label: "COMPANY_HOLIDAY"
    },
    {
        value: "CLIENT_HOLIDAY",
        label: "CLIENT_HOLIDAY"
    },
];



export const LEAVE_DURATIONS = [
    {
        value: "FULL_DAY",
        label: "Full Day"
    },
    {
        value: "FIRST_HALF",
        label: "First Half (AM)"
    },
    {
        value: "SECOND_HALF",
        label: "Second Half (PM)"
    },
];

export const DEFAULT_ACTIVITY_STATUS = "IN_PROGRESS";
export const DEFAULT_LEAVE_PLAN_STATUS = "APPLIED";
export const DEFAULT_LEAVE_PLAN_TYPE = "LEAVE";
export const DEFAULT_LEAVE_DURATION = "FULL_DAY";
// ----------------------------------------------------------------------------

/**
 * All constants representing roles an user can have
 */

export const ROLE_ADMIN = "ADMIN";
export const ROLE_MASTER_DATA_ADMIN = "MASTER_DATA_ADMIN";
export const ROLE_CLIENT = "CLIENT";
export const ROLE_ACCOUNT = "ACCOUNT";
export const ROLE_EMPLOYEE_MANAGER = "EMPLOYEE_MANAGER";
export const ROLE_EMPLOYEE_MEMBER = "EMPLOYEE_MEMBER";
// @since NEXT_VERSION — BDM (Business Development Manager): read-only access to assigned accounts (HRMS-116).
export const ROLE_BDM = "BDM";

// Display names for roles whose stored value reads badly on screen. HRMS-116 asks for BDM to be
// shown as "BDM / Client Relationship Manager"; the stored value stays ROLE_BDM, because renaming
// that means a user-auth migration plus every call site that tests for it.
// @since NEXT_VERSION
const ROLE_LABELS = {
  [ROLE_BDM]: "BDM / Client Relationship Manager",
};

/** The name to show a user for a role, falling back to the stored value. @since NEXT_VERSION */
export const roleLabel = (role) => ROLE_LABELS[role] ?? role ?? "";

// ----------------------------------------------------------------------------

/**
 * Landing routes a signed-in user can be sent to after login
 */

// @since NEXT_VERSION
export const ROUTE_DASHBOARD = "/dashboard";
// @since NEXT_VERSION
export const ROUTE_MASTERS = "/masters";
// @since NEXT_VERSION
export const ROUTE_MEMBERS = "/members";

// ----------------------------------------------------------------------------

/**
 * All constants representing keys to be stored in the browser session store
 */

export const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY || '';
export const SESSION_KEY_TOKEN = "token";
export const SESSION_KEY_IS_LOGIN = "isLogin";
export const SESSION_KEY_LOGGED_IN_USER = "loggedInUser";
export const SESSION_KEY_CURRENT_MEMBER = "currentMember";
export const SESSION_KEY_CURRENT_MANAGER = "currentManager";
export const SESSION_KEY_CURRENT_CLIENT = "currentClient";
export const SESSION_KEY_MEMBER_CLIENTS = "memberClients";

// ----------------------------------------------------------------------------

export const EXPECTED_HOURS_SPENT_PER_DAY = 8;
export const MAXIMUM_ACTIVITY_HOURS_PER_DAY = 12;
// A timesheet is usually submitted a few days before the period it covers ends, so activities may
// be dated up to this many calendar days past today.
// @since NEXT_VERSION
export const ACTIVITY_FUTURE_DAYS_ALLOWED = 10;
// A timesheet can be corrected for a while after the fact, but not indefinitely: an activity may
// not be dated further back than this many calendar months before today.
// @since NEXT_VERSION
export const ACTIVITY_PAST_MONTHS_ALLOWED = 3;
// Mirrors _activity.client_ticket_ref VARCHAR(100).
// @since NEXT_VERSION
export const MAX_CLIENT_TICKET_REF_LENGTH = 100;

// What the dialog will take for a description. Shorter than the column (VARCHAR(500)) because it
// is the validators' rule, not the schema's, that a description stays under 255 characters.
// @since NEXT_VERSION
export const MAX_ACTIVITY_DESCRIPTION_LENGTH = 254;

// Rows one Add Activity dialog may hold at a time. Deliberately well under what the endpoint
// takes — ActivityService.MAX_BULK_ENTRIES is a calendar month — because a table that long stops
// being reviewable before it is saved, and the day totals below it stop fitting on one line.
// @since NEXT_VERSION
export const MAX_ACTIVITY_ROWS_PER_DIALOG = 5;

// Rows per page every table opens with, server-paginated or not. Users can still pick another size.
// @since NEXT_VERSION
export const DEFAULT_TABLE_PAGE_SIZE = 25;

// Total hours one owner may hold on one day across every activity, already-saved rows included.
// Must match ActivityService.MAX_ACTIVITY_HOURS_PER_DAY. Distinct from
// MAXIMUM_ACTIVITY_HOURS_PER_DAY (12), which caps a single entry.
// @since NEXT_VERSION
export const MAX_ACTIVITY_HOURS_PER_OWNER_PER_DAY = 24;

// Mirror client_schema._project (V7__create_project.sql). Named here rather than repeated in the
// dialog and the validator so the two cannot drift from the column widths or from each other.
// @since NEXT_VERSION
export const MAX_PROJECT_CODE_LENGTH = 20;
// @since NEXT_VERSION
export const MAX_PROJECT_NAME_LENGTH = 100;
// @since NEXT_VERSION
export const MAX_PROJECT_DESCRIPTION_LENGTH = 255;

export const DATE_FORMAT_DISPLAY = "DD-MM-YYYY";

// Shared stable empty-array reference. Use as the default value for array
// props/selectors that would otherwise create a new [] every render and
// destabilize useEffect/useMemo dependency arrays.
export const EMPTY_ARRAY = Object.freeze([]);